import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { SampleScenarioTourService } from '../../core/onboarding/sample-scenario-tour.service';
import {
  InstalledSampleScenario,
  SampleScenarioService,
} from '../../core/onboarding/sample-scenario.service';
import {
  SAMPLE_SCENARIOS,
  SAMPLE_SCENARIO_IDS,
  SampleScenarioDefinition,
  SampleScenarioId,
} from '../../core/onboarding/sample-scenarios';
import { LoggerService } from '../../core/services/logger.service';
import { PermissionService } from '../../core/services/permission.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { TourService } from '../../core/tour/tour.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

interface ScenarioCard {
  def: SampleScenarioDefinition;
  installed: InstalledSampleScenario | null;
}

/**
 * Getting started: pick a ready-made sample scenario (security patrol,
 * janitorial, HVAC maintenance), which seeds its form, checkpoints and
 * reports and starts a guided tour of scan → submit → report. Each scenario
 * can be re-toured or removed (with all data recorded against it) here, and
 * users who'd rather start from a blank slate can go straight to the form
 * builder.
 *
 * New orgs land here after signup (see HomeComponent's redirect); it's also
 * reachable any time from the "Getting started" nav item.
 */
@Component({
  selector: 'app-onboarding',
  standalone: true,
  imports: [TranslatePipe, ButtonComponent, IconComponent, SpinnerComponent],
  templateUrl: './onboarding.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './onboarding.component.css',
})
export class OnboardingComponent implements OnInit {
  private router = inject(Router);
  private samples = inject(SampleScenarioService);
  private sampleTour = inject(SampleScenarioTourService);
  private tour = inject(TourService);
  private permissions = inject(PermissionService);
  private toast = inject(ToastService);
  private translate = inject(TranslationService);
  private userStorage = inject(UserStorageService);
  private logger = inject(LoggerService).getLogger('onboarding');

  loading = signal(true);
  cards = signal<ScenarioCard[]>([]);
  /** Scenario with an apply/remove in progress (one at a time). */
  busy = signal<SampleScenarioId | null>(null);
  /** Scenario whose "remove sample data" confirmation is showing. */
  confirmingRemove = signal<SampleScenarioId | null>(null);

  /** Seeding creates forms, checkpoints and reports, so it needs those permissions. */
  readonly canSetUp = computed(
    () => this.permissions.has('asset.create') && this.permissions.has('form.create'),
  );

  async ngOnInit(): Promise<void> {
    await this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      const installed = await this.samples.getInstalled();
      this.cards.set(
        SAMPLE_SCENARIO_IDS.map((id) => ({
          def: SAMPLE_SCENARIOS[id],
          installed: installed.get(id) ?? null,
        })),
      );
    } catch (err) {
      this.logger.error('Failed to load sample scenarios:', err);
      this.toast.error(this.translate.translate('onboarding.errors.load'));
    } finally {
      this.loading.set(false);
    }
  }

  /** Seeds the scenario (if needed) and starts its tour. */
  async tryScenario(id: SampleScenarioId): Promise<void> {
    if (this.busy()) return;
    this.busy.set(id);
    try {
      const result = await this.samples.apply(id);
      if (!result.success && result.reason !== 'already_installed') return;
      this.markOnboardingSeen();
      await this.sampleTour.start(id);
    } catch (err) {
      this.logger.error(`Failed to set up sample scenario '${id}':`, err);
      this.toast.error(this.translate.translate('onboarding.errors.apply'));
      await this.refresh();
    } finally {
      this.busy.set(null);
    }
  }

  async startTour(id: SampleScenarioId): Promise<void> {
    this.markOnboardingSeen();
    const started = await this.sampleTour.start(id);
    if (!started) await this.refresh();
  }

  askRemove(id: SampleScenarioId): void {
    this.confirmingRemove.set(id);
  }

  cancelRemove(): void {
    this.confirmingRemove.set(null);
  }

  async confirmRemove(id: SampleScenarioId): Promise<void> {
    if (this.busy()) return;
    this.busy.set(id);
    try {
      await this.samples.remove(id);
      if (this.tour.tourId() === `sample-scenario:${id}`) {
        this.tour.end();
      }
      this.toast.success(this.translate.translate('onboarding.removed'));
    } catch (err) {
      this.logger.error(`Failed to remove sample scenario '${id}':`, err);
      this.toast.error(this.translate.translate('onboarding.errors.remove'));
    } finally {
      this.confirmingRemove.set(null);
      this.busy.set(null);
      await this.refresh();
    }
  }

  removeParams(card: ScenarioCard): Record<string, string> {
    return {
      checkpoints: String(card.installed?.checkpointIds.length ?? 0),
      submissions: String(card.installed?.submissionCount ?? 0),
    };
  }

  /** Blank-slate path: build a form first (then come back here). */
  startFromScratch(): void {
    this.markOnboardingSeen();
    void this.router.navigate(['/forms/new'], { queryParams: { returnUrl: '/onboarding' } });
  }

  skipOnboarding(): void {
    this.markOnboardingSeen();
    void this.router.navigate(['/']);
  }

  /** Stops Home from redirecting here on every visit. */
  private markOnboardingSeen(): void {
    this.userStorage.setTypedItem('onboarding_dismissed', true);
  }
}
